import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Loader2 } from 'lucide-react';
import 'react-phone-number-input/style.css';
import ProfileHeader from '../../components/profile/profilePage/ProfileHeader';
import ContactInformation from '../../components/profile/profilePage/ContactInformation';
import AddressDetails from '../../components/profile/profilePage/AddressDetails';
import AdditionalInformation from '../../components/profile/profilePage/AdditionalInformation';
import SaveButton from '../../components/profile/profilePage/SaveButton';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';

const ProfilePage = () => {
  const { state } = useAppContext();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    district: '',
    city: '',
    parish: '',
    postalCode: '',
    country: '',
    street: '',
    doorNumber: '',
    apartment: '',
    nif: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/users/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch profile');

      const data = await response.json();
      setFormData({
        email: data.email || '',
        phone: data.phone || '',
        country: data.country || '',
        district: data.district || '',
        city: data.city || '',
        parish: data.parish || '',
        postalCode: data.postalCode || '',
        street: data.street || '',
        doorNumber: data.doorNumber || '',
        apartment: data.apartment || '',
        nif: data.nif || '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      country: selectedOption.value,
    }));
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/users/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update profile');

      showAlert({
        title: t("alert.success"),
        description: t("alert.success.profileUpdate"),
        variant: 'success'
      })
    } catch (error) {
      console.error('Error updating profile:', error);
      showAlert({
        title: t("alert.error"),
        description: t("alert.error.profileUpdate"),
        variant: 'destructive'
      })
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="-mt-16 min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <ProfileHeader
          user={state.user}
          email={formData.email}
        />

        <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
          <ContactInformation
            email={formData.email}
            phone={formData.phone}
            onEmailChange={handleChange}
            onPhoneChange={handlePhoneChange}
            translations={t}
          />

          <AddressDetails
            formData={formData}
            onChange={handleChange}
            onCountryChange={handleCountryChange}
            translations={t}
          />

          <AdditionalInformation
            nif={formData.nif}
            onChange={handleChange}
            translations={t}
          />

          <div className="flex justify-center sm:justify-end mt-6">
            <SaveButton isSaving={isSaving} translations={t} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;